import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { useGlobalToast } from'../../../stopOldToastGlobally.js';
import { toast } from "react-toastify";
import { Form, Spinner, Row, Col, Card, Badge } from "react-bootstrap";
import Button from "@mui/material/Button";
import debounce from "lodash/debounce";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { FiDownload } from "react-icons/fi";
import {
  Briefcase,
  Phone,
  IdCard,
  UserLarge,
  TriangleExclamation,
  PencilLine,
  CircleInfo,
} from "../../../components/icons/Icons.js";
import BasicJob from "./component/basicJob.js";
import Contact from "./component/Contact.js";
import GovernmentIds from "./component/governmentIds.js";
import Personal from "./component/Personal.js";
import Emergency from "./component/Emergency.js";
import { MdClear } from "react-icons/md";
import { Accordion, OverlayTrigger, Tooltip } from "react-bootstrap";
import Widgets from "../../../components/Widgets";
import TableSkeleton from "../../../components/table/TableSkeleton.js";
import Pagination from "../../../components/pagination/pagination.js";
import CustomTab from "../../../components/Tabs/CustomTab.js";
import {
  downloadCSV,
  getEmployeeFormattedData,
  getCurrentDate,
  getInitials,
  getInitialColor,
  generateSNoOnj,
} from "../../../services/commonServices";
import {
  commonSaveData,
  commonGetData,
  initState,
  initCommonState,
} from "../../../redux/slices/commonSlice";
import {
  getEmployeeStaffListPath,
  deactivateEmployeePath,
} from "../../../services/config";
import { Link, useNavigate } from "react-router-dom";
import { Switch } from "@mui/material";

// Import jsPDF and xlsx for PDF & Excel
import jsPDF from "jspdf";
import * as XLSX from "xlsx";

const StaffDetails = ({
  dispatch,
  user,
  state,
  employeeState,
  branchList,
  designationList,
  departmentList,
}) => {
  const navigate = useNavigate();
    const customToast = useGlobalToast(); 

  const [filteredUsersList, setFilteredUsersList] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const [currentData, setCurrentData] = useState([]);
  const [jobTitle, setJobTitle] = useState("");
  const [departmentId, setDepartmentId] = useState(0);
  const [branch, setBranch] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedRow, setSelectedRow] = useState({});
  const [isNoticePeriodPopup, setIsNoticePeriodPopup] = useState(false);
  const [noticePeriodData, setNoticePeriodData] = useState({});

  // Checkbox & Download State
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [allChecked, setAllChecked] = useState(false);
  const [downloadType, setDownloadType] = useState("csv");

  // Caching
  const cachedDataRef = useRef(null);
  const lastParamsRef = useRef(null);

  const employee = useMemo(() => ({}), []);

  const buildPath = useCallback(() => {
    return `${getEmployeeStaffListPath}${user.user_id}&branchId=${branch}&departmentId=${departmentId}&jobTitle=${jobTitle}`;
  }, [user.user_id, branch, departmentId, jobTitle]);

  const fetchEmployeeList = useCallback(() => {
    const currentPath = buildPath();
    if (lastParamsRef.current === currentPath && cachedDataRef.current) {
      setFilteredUsersList(cachedDataRef.current);
      return;
    }
    dispatch(commonGetData(currentPath));
    lastParamsRef.current = currentPath;
  }, [buildPath, dispatch]);

  useEffect(() => {
    fetchEmployeeList();
  }, [fetchEmployeeList]);

  useEffect(() => {
    if (state.commonGetSuccess && state.commonGetData) {
      const data = state.commonGetData || [];
      cachedDataRef.current = data;
      setFilteredUsersList(data);
      dispatch(initState());
    }
    if (state.commonGetError) {
      toast.error("Failed to load employees");
      dispatch(initState());
    }
    if (state.commonSuccess || employeeState.empSuccess) {
      fetchEmployeeList();
      dispatch(initCommonState());
    }
  }, [state, employeeState, dispatch, fetchEmployeeList]);

  useEffect(() => {
    setSelectedRows([]);
    setAllChecked(false);
  }, [filteredUsersList]);

  useEffect(() => {
    if (currentData.length === 0) {
      setAllChecked(false);
      return;
    }
    const allOnPageSelected = currentData.every((item) =>
      selectedRows.includes(item.staffId)
    );
    setAllChecked(allOnPageSelected);
  }, [selectedRows, currentData]);

  const debouncedSetJobTitle = useCallback(debounce((val) => setJobTitle(val), 600), []);
  const debouncedSetBranch = useCallback(debounce((val) => setBranch(val), 600), []);
  const debouncedSetDepartment = useCallback(debounce((val) => setDepartmentId(val), 600), []);

  const handleSearch = useCallback(
    debounce((query) => {
      if (!cachedDataRef.current) return;
      const lower = query.trim().toLowerCase();
      if (!lower) {
        setFilteredUsersList(cachedDataRef.current);
        return;
      }
      const results = cachedDataRef.current.filter(
        (emp) =>
          emp.employee_id?.toLowerCase().includes(lower) ||
          emp.full_name?.toLowerCase().includes(lower) ||
          emp.mobile_number?.includes(lower)
      );
      setFilteredUsersList(results);
    }, 400),
    []
  );

  const searchUser = (e) => {
    const val = e.target.value;
    setFilterUser(val);
    handleSearch(val);
  };

  const clearFilter = () => {
    setFilterUser("");
    setJobTitle("");
    setDepartmentId(0);
    setBranch(0);
    setSelectedRows([]);
    setAllChecked(false);
    cachedDataRef.current = null;
    lastParamsRef.current = null;
    fetchEmployeeList();
  };

  const handleToggleChange = (e) => {
    const checked = e.target.checked;
    setShowCheckboxes(checked);
    if (!checked) {
      setSelectedRows([]);
      setAllChecked(false);
    }
  };

  const handleAllCheck = (e) => {
    const checked = e.target.checked;
    setAllChecked(checked);
    setSelectedRows(checked ? currentData.map((i) => i.staffId) : []);
  };

  const handleRowCheck = (e, staffId) => {
    e.stopPropagation();
    const checked = e.target.checked;
    setSelectedRows((prev) =>
      checked ? [...prev, staffId] : prev.filter((id) => id !== staffId)
    );
  };

  const handleDownloadTypeChange = (e) => {
    setDownloadType(e.target.value);
  };

  // === PDF DOWNLOAD - FULLY WORKING ===
  const downloadPDF = useCallback((data, filename = "Employee_Report") => {
    if (!data || data.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const doc = new jsPDF("l", "pt", "a4"); // Landscape
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    let y = 80;

    // Title
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("Employee Details Report", pageWidth / 2, y, { align: "center" });
    y += 40;

    // Subtitle
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${getCurrentDate()}`, margin, y);
    doc.text(`Total Records: ${data.length}`, margin + 400, y);
    y += 50;

    // Table Headers
    const headers = ["SNo", "Emp ID", "Name", "Designation", "Department", "Branch", "Status"];
    const colWidths = [40, 80, 150, 120, 120, 100, 70];
    let x = margin;

    doc.setFillColor(79, 70, 229);
    doc.rect(margin - 10, y - 20, pageWidth - 80, 35, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");

    headers.forEach((h, i) => {
      doc.text(h, x, y);
      x += colWidths[i];
    });

    y += 45;

    // Rows
    doc.setTextColor(0);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    data.forEach((item, index) => {
      if (y > pageHeight - 80) {
        doc.addPage();
        y = 80;
      }

      x = margin;
      const row = [
        (index + 1).toString(),
        item.employee_id || "-",
        item.full_name || "-",
        item.job_title || "-",
        item.departmentName || "-",
        item.branchName || "-",
        item.status == "1" ? "Active" : "Inactive",
      ];

      if (index % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(margin - 10, y - 20, pageWidth - 80, 30, "F");
      }

      row.forEach((cell, i) => {
        doc.text(cell.substring(0, 25), x, y);
        x += colWidths[i];
      });

      y += 30;
    });

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 30, { align: "center" });
    }

    doc.save(`${filename}_${getCurrentDate()}.pdf`);
    toast.success("PDF downloaded successfully!");
  }, []);

  // === Excel Download ===
  const downloadExcel = useCallback((data, filename = "Employee_Report") => {
    if (!data || data.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const wsData = [
      ["SNo", "Employee ID", "Full Name", "Designation", "Department", "Branch", "Status", "Joining Date", "Email", "Phone"],
      ...data.map((emp, i) => [
        i + 1,
        emp.employee_id || "-",
        emp.full_name || "-",
        emp.job_title || "-",
        emp.departmentName || "-",
        emp.branchName || "-",
        emp.status == "1" ? "Active" : "Inactive",
        emp.date_of_join || "-",
        emp.email || "-",
        emp.mobile_number || "-",
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employees");

    ws["!cols"] = [
      { wch: 6 }, { wch: 15 }, { wch: 25 }, { wch: 20 },
      { wch: 20 }, { wch: 15 }, { wch: 10 }, { wch: 15 },
      { wch: 30 }, { wch: 15 }
    ];

    XLSX.writeFile(wb, `${filename}_${getCurrentDate()}.xlsx`);
    toast.success("Excel downloaded successfully!");
  }, []);

  // === Main Download Handler ===
  const downloadBulkData = () => {
    let dataToExport = filteredUsersList;

    if (showCheckboxes && selectedRows.length > 0) {
      dataToExport = filteredUsersList.filter((emp) => selectedRows.includes(emp.staffId));
    }

    if (dataToExport.length === 0) {
      toast.info("Please select at least one employee");
      return;
    }

    const filename = `Employee_Report_${getCurrentDate()}`;

    switch (downloadType) {
      case "pdf":
        downloadPDF(dataToExport, filename);
        break;
      case "xlsx":
        downloadExcel(dataToExport, filename);
        break;
      case "csv":
      default:
        downloadCSV(getEmployeeFormattedData(dataToExport), `${filename}.csv`);
        break;
    }
  };

  const toggleAccordion = (data) => {
    setSelectedRow(data);
    setExpandedRow(expandedRow === data.employee_id ? null : data.employee_id);
  };

  const handleActiveInactiveChange = (event, rowData) => {
    event.stopPropagation();
    dispatch(
      commonSaveData({
        path: deactivateEmployeePath,
        data: {
          userId: user.user_id,
          staffId: rowData.staffId,
          isActiveUser: event.target.value,
        },
      })
    );
  };

  const noticePeriodPopupOpen = () => setIsNoticePeriodPopup(true);
  const noticePeriodPopupClose = () => setIsNoticePeriodPopup(false);

  const handleNoticePeriod = (item) => {
    setNoticePeriodData(item);
    noticePeriodPopupOpen();
  };

  const navigateUser = (id) => navigate(`/empmanagement/user-info/${id}`);

  const downloadIndividualStaffDetail = (data) => {
    downloadCSV(
      getEmployeeFormattedData([data]),
      `Individual_Staff_Detail_${getCurrentDate()}.csv`
    );
  };

  const tabArray = [
    { icon: <Briefcase class_name={""} />, label: "Basic & Job" },
    { icon: <Phone class_name={""} />, label: "Contacts" },
    { icon: <IdCard class_name={""} />, label: "Identity" },
    { icon: <UserLarge class_name={""} />, label: "Personal" },
    { icon: <TriangleExclamation class_name={""} />, label: "Emergency" },
  ];

  const components = [
    <BasicJob data={selectedRow} />,
    <Contact data={selectedRow} />,
    <GovernmentIds data={selectedRow} />,
    <Personal data={selectedRow} />,
    <Emergency data={selectedRow} />,
  ];

  return (
    <div>
      {/* Search & Toggle */}
      <div className="search-box flex-space-between mt-m-40">
        <Form className="d-flex align-items-center justify-content-start gap-2 flex-wrap">
          <Form.Group>
            <Form.Control
              placeholder="Search by Employee Name & ID"
              onChange={searchUser}
              value={filterUser}
              className="universal-input-one"
              style={{ width: "250px" }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Select
              onChange={(e) => debouncedSetJobTitle(e.target.value)}
              value={jobTitle}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value="">Select Job Title</option>
              {designationList?.map((row) => (
                <option key={row.value} value={encodeURIComponent(row.label)}>
                  {row.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Select
              onChange={(e) => debouncedSetBranch(e.target.value)}
              value={branch}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={0}>Select Branch</option>
              {branchList?.map((row) => (
                <option key={row.branchId} value={row.branchId}>
                  {row.branch_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Select
              onChange={(e) => debouncedSetDepartment(e.target.value)}
              value={departmentId}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={0}>Select Department</option>
              {departmentList?.map((row) => (
                <option key={row.value} value={row.value}>
                  {row.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {(filterUser || jobTitle || departmentId !== 0 || branch !== 0) && (
            <button type="button" className="clear" onClick={clearFilter}>
              <MdClear /> Clear
            </button>
          )}
        </Form>

        <div className="d-flex align-items-center gap-3">
          <span className="text-white font-14">Enable Selection</span>
          <Switch checked={showCheckboxes} onChange={handleToggleChange} color="primary" />
        </div>
      </div>

      {/* Format Selector */}
{showCheckboxes && (
  <div className="mt-2 px-0">
    <div className="d-flex align-items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: "rgba(128, 128, 128, 0.1)" }}>
      <span className="font-12 font-weight-500" style={{ color: "#6c757d" }}>
        Format
      </span>

      <Form.Select
        value={downloadType}
        onChange={handleDownloadTypeChange}
        className="form-select-sm"
        style={{
          width: "auto",
          minWidth: "70px",
          fontSize: "12px",
          padding: "4px 8px",
          borderRadius: "6px",
          cursor: "pointer",
          backgroundColor: "transparent",
          border: "1px solid #dee2e6"
        }}
      >
        <option value="csv">CSV</option>
        <option value="pdf">PDF</option>
        <option value="xlsx">XLSX</option>
      </Form.Select>

      {selectedRows.length > 0 && (
        <span className="font-12 font-weight-500" style={{ color: "#28a745" }}>
          {selectedRows.length} selected
        </span>
      )}
    </div>
  </div>
)}   {/* Table Header - FIXED with CSS Grid */}
     <div
  className="employee-grid-header mt-4 mb-3"
  style={{
    marginTop: "-20px",
    padding: "0 40px",
    display: "grid",
    gridTemplateColumns: showCheckboxes ?
      "50px 60px 120px 40px 1fr 150px 150px 200px" :
      "60px 120px 40px 1fr 150px 150px 200px",
    gap: "15px",
    alignItems: "center",
    width: "100%"
  }}
>
  {showCheckboxes && (
    <span className="font-14 font-weight-400 color-gray emp-checkbox" style={{ justifySelf: "center" }}>
      <Form.Check
        type="checkbox"
        checked={allChecked}
        onChange={handleAllCheck}
        style={{ transform: "scale(1.1)" }}
      />
    </span>
  )}
  <span className="font-14 font-weight-400 color-gray emp-sno">SNo</span>
  <span className="font-14 font-weight-400 color-gray emp-id">Employee ID</span>
  <div className="font-14 font-weight-400 color-gray" style={{ justifySelf: "center" }}></div>
  <span className="font-14 font-weight-400 color-gray emp-name">Full Name</span>
  <span className="font-14 font-weight-400 color-gray emp-job">Designation</span>
  <span className="font-14 font-weight-400 color-gray emp-branch">Branch</span>
  <div className="font-14 font-weight-400 color-gray emp-actions" style={{ justifySelf: "flex-end" }}>Actions</div>
</div>

      {/* Employee List */}
      <Widgets
        body={
          <div style={{ width: "100%", paddingBottom: "100px", marginTop: "-20px" }}>
            {state.commonGetLoading ? (
              <TableSkeleton />
            ) : filteredUsersList.length < 1 ? (
              <div style={{ padding: "50px", textAlign: "center" }}>
                <span className="color-white font-18">Data not found</span>
              </div>
            ) : currentData.length < 1 ? (
              <div style={{ padding: "100px", textAlign: "center" }}>
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <Accordion activeKey={expandedRow} className="padding-bottom-50px cust-accrdn">
                {currentData.map((item, idx) => {
                  const isSelected = selectedRows.includes(item.staffId);
                  return (
                    <Accordion.Item
                      eventKey={item.employee_id}
                      className="mb-2 margin-bottom-10 custom-accordian-item"
                      key={item.employee_id}
                    >
                      <Accordion.Header
                        className="custom-accordian-header"
                        style={{
                          borderBottomLeftRadius: expandedRow !== item.employee_id ? "8px" : "0px",
                          borderBottomRightRadius: expandedRow !== item.employee_id ? "8px" : "0px",
                        }}
                        onClick={() => toggleAccordion(item)}
                      >
                        <div
                          className="employee-grid-row"
                          style={{
                            padding: "0 40px",
                            display: "grid",
                            gridTemplateColumns: showCheckboxes ?
                              "50px 60px 120px 40px 1fr 150px 150px 200px" :
                              "60px 120px 40px 1fr 150px 150px 200px",
                            gap: "15px",
                            alignItems: "center",
                            width: "100%"
                          }}
                        >
                          {showCheckboxes && (
                            <div
                              className="emp-checkbox"
                              onClick={(e) => e.stopPropagation()}
                              style={{ justifySelf: "center" }}
                            >
                              <Form.Check
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handleRowCheck(e, item.staffId)}
                                style={{ transform: "scale(1.1)" }}
                              />
                            </div>
                          )}
                          <span className="color-gray emp-sno font-14">{idx + 1}</span>
                          <span className="color-gray emp-id font-14">#{item.employee_id || "-"}</span>
                          <div
                            className="emp-avatar"
                            style={{
                              backgroundColor: getInitialColor(item.full_name),
                              width: "32px",
                              height: "32px",
                              fontSize: "12px",
                              justifySelf: "center"
                            }}
                          >
                            {getInitials(item.full_name) ?? "-"}
                          </div>
                          <span
                            className="emp-name color-white font-14"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateUser(item.staffId);
                            }}
                          >
                            {item.full_name ?? "-"}
                          </span>
                          <span className="emp-job color-white font-14">{item.job_title ?? "-"}</span>
                          <span className="emp-branch color-white font-14">{item.branchName ?? "-"}</span>
                          <div className="emp-actions color-white d-flex align-items-center gap-3" style={{ justifySelf: "flex-end" }}>
                            <select
                              className={item.status == "1" ? "statusGreen" : "statusOrange"}
                              value={item.status}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleActiveInactiveChange(e, item);
                              }}
                              style={{ fontSize: "12px", padding: "4px 8px" }}
                            >
                              <option value="1">Active</option>
                              <option value="0">InActive</option>
                            </select>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Notice Period</Tooltip>}>
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNoticePeriod(item);
                                }}
                              >
                                <CircleInfo class_name={"font-18 text-info"} />
                              </span>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                              <Link
                                to={`user-info/${item.staffId}`}
                                className="edit-link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <PencilLine class_name="edit-icon font-18" />
                              </Link>
                            </OverlayTrigger>
                          </div>
                        </div>
                      </Accordion.Header>

                      <Accordion.Body className="custom-accordian-body">
                        <div className="p-2">
                          <CustomTab tabArray={tabArray} tabWidth="250px" components={components} />
                        </div>
                        <div className="accordion-footer mt-4">
                          <button
                            className="universal-btn border-radius-8px"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadIndividualStaffDetail(item);
                            }}
                          >
                            Download
                          </button>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}
          </div>
        }
      />

      {/* Pagination + Download Button */}
      {filteredUsersList.length > 0 && (
        <Pagination
          data={generateSNoOnj(filteredUsersList)}
          setCurrentData={setCurrentData}
          className="bottom-pagination mt-8"
          action={
            <Button
              onClick={downloadBulkData}
              variant="contained"
              startIcon={<FiDownload />}
              className="universal-btn"
              disabled={showCheckboxes && selectedRows.length === 0}
            >
              {showCheckboxes && selectedRows.length > 0
                ? `Download Selected (${selectedRows.length}) as ${downloadType.toUpperCase()}`
                : "Download All as CSV"}
            </Button>
          }
        />
      )}

      {/* Notice Period Dialog */}
      <Dialog fullWidth maxWidth="xl" open={isNoticePeriodPopup} onClose={noticePeriodPopupClose}>
        <Form>
          <DialogTitle className="dialog-title">
            <div className="dialog-title-icon">
              <span className="close-icon" onClick={noticePeriodPopupClose}>X</span>
              <span className="title">Notice Period</span>
            </div>
            <button className="universal-btn" type="submit">Save Changes</button>
          </DialogTitle>
          <DialogContent style={{ paddingTop: "15px" }} className="notice-period-bg">
            <div className="notice-page">
              <div className="setting-border height-auto setting-border-one">
                <div className="setting-card-header">
                  <h3 className="color-white font-weight-200">Notice Period</h3>
                </div>
                <Row className="px-4 mt-4 mb-4">
                  <Col md={8} lg={9}>
                    <div className="setting-switch-enbl-dsbl setting-border-one mb-4">
                      <div className="d-flex align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                          <div
                            className="emp-avatar"
                            style={{ backgroundColor: getInitialColor(noticePeriodData.full_name) }}
                          >
                            {getInitials(noticePeriodData.full_name)}
                          </div>
                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <h5 className="mb-0 fw-semibold color-white">
                                {noticePeriodData.full_name} 
                              </h5>
                              <Badge bg="success" pill className="align-middle">
                                {noticePeriodData.status == "1" ? "Active" : "InActive"}
                              </Badge>
                            </div>
                            <div className="small color-gray">ID: {noticePeriodData.employee_id}</div>
                          </div>
                        </div>
                        <div className="d-flex align-items-center justify-content-between gap-2" style={{ width: "100%" }}>
                          <div>
                            <div className="color-white font-16">Department</div>
                            <h5 className="color-gray font-14 mt-1">{noticePeriodData.departmentName}</h5>
                          </div>
                          <div>
                            <div className="color-white font-16">Branch</div>
                            <h5 className="color-gray font-14 mt-1">{noticePeriodData.branchName}</h5>
                          </div>
                          <div>
                            <div className="color-white font-16">Designation</div>
                            <h5 className="color-gray font-14 mt-1">{noticePeriodData.job_title}</h5>
                          </div>
                          <div>
                            <div className="color-white font-16">Joining Date</div>
                            <h5 className="color-gray font-14 mt-1">{noticePeriodData.date_of_join}</h5>
                          </div>
                          <div>
                            <div className="color-white font-16">Total Tenure</div>
                            <h5 className="color-gray font-14 mt-1">{employee.tenure}</h5>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="setting-switch-enbl-dsbl setting-border-one mb-4">
                      <h3 className="color-white font-weight-200">Current Notice Period</h3>
                      <Row className="align-items-center mt-3">
                        <Col md="4" className="display-6 fw-bold color-gray">30 days</Col>
                        <Col md>
                          <Row className="gy-3">
                            <Col xs={6} md={3}>
                              <div className="color-white font-16">Policy Type</div>
                              <h5 className="color-gray font-weight-200 font-14 mt-1">Default</h5>
                            </Col>
                            <Col xs={6} md={3}>
                              <div className="color-white font-16">Last Updated</div>
                              <h5 className="color-gray font-weight-200 font-14 mt-1">14/10/2025</h5>
                            </Col>
                            <Col xs={6} md={3}>
                              <div className="color-white font-16">Effective From</div>
                              <h5 className="color-gray font-weight-200 font-14 mt-1">14/10/2025</h5>
                            </Col>
                            <Col xs={6} md={3}>
                              <div className="color-white font-16">Applies To</div>
                              <h5 className="color-gray font-weight-200 font-14 mt-1">Company Policy</h5>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </div>

                    <div className="setting-switch-enbl-dsbl setting-border-one mb-4">
                      <h3 className="color-white font-weight-200">Notice Period Policy</h3>
                      <Button variant="link" className="p-0 mt-3 color-white">
                        Switch to Custom Policy to edit notice period fields
                      </Button>
                      <Row className="g-3 align-items-stretch mt-2">
                        <Col md>
                          <Card className="h-100 variant-select selected">
                            <Card.Body className="d-flex align-items-center justify-content-between">
                              <div>
                                <div className="color-white fw-semibold">Default Policy</div>
                                <div className="color-gray small mt-1">Apply company-wide policy</div>
                              </div>
                              <Form.Check type="radio" name="policy" defaultChecked aria-label="Default policy" />
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md>
                          <Card className="h-100 variant-select">
                            <Card.Body className="d-flex align-items-center justify-content-between">
                              <div>
                                <div className="color-white fw-semibold">Custom Policy</div>
                                <div className="color-gray small mt-1">Override for this employee</div>
                              </div>
                              <Form.Check type="radio" name="policy" aria-label="Custom policy" />
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  <Col md={4} lg={3}>
                    <div className="setting-switch-enbl-dsbl setting-border-one mb-4">
                      <div className="color-white mb-3 font-weight-200">Notifications</div>
                      <div className="d-flex align-items-center justify-content-start gap-2">
                        <Switch type="switch" id="notify-emps" defaultChecked />
                        <span className="color-white font-14 font-weight-200">Notify employee about this change</span>
                      </div>
                      <div className="d-flex align-items-center justify-content-start gap-2">
                        <Switch type="switch" id="notify-managers" defaultChecked />
                        <span className="color-white font-14 font-weight-200">Notify reporting manager</span>
                      </div>
                      <div className="d-flex align-items-center justify-content-start gap-2">
                        <Switch type="switch" id="send-hrs" />
                        <span className="color-white font-14 font-weight-200">Send HR confirmation email</span>
                      </div>
                    </div>

                    <div className="setting-switch-enbl-dsbl setting-border-one warning-bdr">
                      <div className="d-flex gap-3">
                        <div className="reminder-dot mt-1" aria-hidden />
                        <div>
                          <div className="fw-semibold color-white font-weight-200 mb-2">Policy Reminder</div>
                          <div className="small color-gray">
                            Minimum notice period is 15 days. Managers require 45 days.
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </DialogContent>
        </Form>
      </Dialog>
    </div>
  );
};

export default StaffDetails;